import { useMemo, useRef } from 'react';
import { RouteName } from '@mews-commander/enums';
import { GuestProfile, MatchedGuestProfiles } from '@mews-commander/guest-profile';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { lt, showSuccessAlert, useGainsightService, useSafeUrlState } from '@mews/framework';
import { showRouteInModal } from '../../../../../../../../modules/app/actions/common';
import { getGainSightEventObject, MatchAndMergeGainSightEvents } from '../../../../../GainSightEvents';
import { accountMergeUrlQueryParamSchema } from '../../../../../schema';
import { MergeMode } from '../../../../../types';
import { useDeclineMatch, useMergeGuestProfiles } from '../mutations';
import { MergeFormSchema, transformFormValues } from '../schema';

export interface SubmitHandlerParams {
    mergeMode: MergeMode;
    handleConfirmationDialogClose: () => void;
    handleKeepSeparateDialogClose: () => void;
    leftProfile: GuestProfile;
    rightProfile: GuestProfile;
    navigateToNextMatch: (usePrevious: boolean) => void;
    matchedGuestProfiles: MatchedGuestProfiles;
}

const navigateToGuestProfile = (profileId: string, dispatch: CommanderThunkDispatch) => {
    dispatch(showRouteInModal(RouteName.CustomerDetail, { id: profileId }));
};

export const useMergeSubmitHandler = ({
    mergeMode,
    handleConfirmationDialogClose,
    handleKeepSeparateDialogClose,
    leftProfile,
    rightProfile,
    navigateToNextMatch,
    matchedGuestProfiles,
}: SubmitHandlerParams) => {
    const gainSightService = useGainsightService();
    const dispatch = useCommanderThunkDispatch();
    const finalProfileId = useRef('');
    const [urlState] = useSafeUrlState(accountMergeUrlQueryParamSchema);

    const handleNavigateOnMergeSuccess = () => {
        if (mergeMode === MergeMode.Single) {
            gainSightService.trackEvent(MatchAndMergeGainSightEvents.MatchAndMergeNavigatedToCustomerProfile, {
                mergeMode,
                customerId: finalProfileId.current,
            });
            navigateToGuestProfile(finalProfileId.current, dispatch);
        } else {
            navigateToNextMatch(true);
        }
    };

    const handleShowSuccessAlert = () => {
        if (mergeMode === MergeMode.Single) {
            showSuccessAlert({
                title: lt('AccountMergeSuccessNotificationTitle'),
            });
        }

        if (mergeMode === MergeMode.Queue) {
            showSuccessAlert({
                closeTimeout: 4000,
                title: lt('AccountMergeSuccessNotificationTitle'),
                description: lt('AccountMergeSuccessNotificationDescription'),
                action: {
                    text: lt('AccountMergeSuccessNotificationAction'),
                    onClick: () => {
                        gainSightService.trackEvent(
                            MatchAndMergeGainSightEvents.MatchAndMergeNavigatedToCustomerProfile,
                            {
                                customerId: finalProfileId.current,
                            }
                        );
                        navigateToGuestProfile(finalProfileId.current, dispatch);
                    },
                },
            });
        }
    };

    const onMergeSuccess = () => {
        handleShowSuccessAlert();
        gainSightService.trackEvent(
            MatchAndMergeGainSightEvents.MatchAndMergeConfirmed,
            getGainSightEventObject(matchedGuestProfiles, mergeMode)
        );
        handleConfirmationDialogClose();
        handleNavigateOnMergeSuccess();
    };

    const { mutate, isPending: isMerging } = useMergeGuestProfiles({
        onSuccess: onMergeSuccess,
    });

    const onDeclineMatchSuccess = () => {
        showSuccessAlert({
            title: lt('AccountMergeKeepSeparateSuccessTitle'),
            description: lt('AccountMergeKeepSeparateSuccessDescription'),
        });

        gainSightService.trackEvent(
            MatchAndMergeGainSightEvents.MatchAndMergeKeepSeparateProfiles,
            getGainSightEventObject(matchedGuestProfiles, mergeMode)
        );

        handleKeepSeparateDialogClose();
        navigateToNextMatch(true);
    };

    const { mutate: mutateDeclineMatch, isPending: isDeclineMatchPending } = useDeclineMatch({
        onSuccess: onDeclineMatchSuccess,
    });

    return useMemo(() => {
        const handleMerge = (data: MergeFormSchema) => {
            const { targetId, guestProfileDetails } = data;
            const sourceId = leftProfile.id === targetId ? rightProfile.id : leftProfile.id;

            const mutationInput = {
                sourceId,
                targetId,
                ...(guestProfileDetails ? { guestProfileDetails: transformFormValues(guestProfileDetails) } : null),
            };
            mutate(mutationInput);
            finalProfileId.current = targetId;
        };

        const handleReviewLater = () => {
            gainSightService.trackEvent(
                MatchAndMergeGainSightEvents.MatchAndMergeReviewLater,
                getGainSightEventObject(matchedGuestProfiles, mergeMode)
            );
            if (mergeMode === MergeMode.Single) {
                gainSightService.trackEvent(MatchAndMergeGainSightEvents.MatchAndMergeClosed, {
                    mergeMode,
                });
                gainSightService.trackEvent(MatchAndMergeGainSightEvents.MatchAndMergeNavigatedToCustomerProfile, {
                    mergeMode,
                    customerId: urlState?.customerId || leftProfile.id,
                });
                navigateToGuestProfile(urlState?.customerId || leftProfile.id, dispatch);
            } else {
                navigateToNextMatch(false);
            }
        };

        const handleDeclineMatch = () => {
            const mutationDeclineInput = { guest1Id: leftProfile.id, guest2Id: rightProfile.id };
            mutateDeclineMatch(mutationDeclineInput);
        };

        return {
            handleMerge,
            isDeclineMatchPending,
            isMerging,
            handleDeclineMatch,
            handleReviewLater,
        };
    }, [
        isDeclineMatchPending,
        isMerging,
        leftProfile.id,
        rightProfile.id,
        mutate,
        gainSightService,
        matchedGuestProfiles,
        mergeMode,
        urlState?.customerId,
        dispatch,
        navigateToNextMatch,
        mutateDeclineMatch,
    ]);
};
